import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
// api
import { postContact, postManualReturn } from 'api/users';
// slices
import { companySelector } from 'slices/company';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
// components
import Head from 'components/metatags_helmet/metatags';
import TitleWithLine from 'components/titleWithLine';
import FeedbackStatus from 'components/userzone/FeedbackStatus';
//styles
import '../../assets/css/staticPages.scss';

const resetValues = {
  names: '',
  company: '',
  email: '',
  phone: '',
  department: '',
  message: '',
  recieveNews: false,
  acceptPolicy: false,
  ibns: '',
  pedido_id: '',
};
const Contact = () => {
  const { t } = useTranslation(['contact', 'card', 'backoffice']);
  const [requestStatus, setRequestStatus] = useState('');
  const [msg, setMsg] = useState('');
  const [showCard, setShowCard] = useState(false);
  const { assignedLibrary } = useAssignedLibrary();
  const title = t('contact');
  const { descripcion_pub, email: correo, telefono } = useSelector(companySelector);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    register,
    getValues,
  } = useForm({
    defaultValues: resetValues,
  });

  const goUp = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    goUp();
  }, []);

  const onSubmit = async (values) => {
    const payload = {
      full_name: values?.names,
      email: values?.email,
      phone_number: values?.phone,
      message: values?.message,
      company_name: values?.company,
      department: values?.department,
      rcv_news_offers_acceptance: values?.recieveNews,
      policies_conditions_acceptance: values?.acceptPolicy,
      ...(values.department === 'returns' && {
        ibns: values?.ibns,
        pedido_id: values?.pedido_id,
      }),
    };
    try {
      const response = values.department === 'returns' ? await postManualReturn(payload) : await postContact(payload);
      const isSuccess =
        (values.department === 'returns' && response.status === 200) ||
        (values.department !== 'returns' && response.status === 201);
      if (isSuccess) {
        setRequestStatus('success');
        setShowCard(true);
        setMsg(t('success'));
        reset(resetValues);
      } else {
        setRequestStatus('error');
        setShowCard(true);
        reset(resetValues);
        setMsg(t('somethingwrong', { ns: 'card' }));
      }
    } catch (error) {
      setTimeout(() => {
        setShowCard(false);
      }, 5000);
    }
  };

  let buttonText = t('send').toUpperCase();

  const handleClose = () => {
    setShowCard(false);
  };

  return (
    <div className="content mr-3">
      <div className="justify-content-center d-flex mainDiv container-fluid contact">
        <Head description={descripcion_pub} title={title} noIndex={true} />
        <Row className="libraries-row justify-content-center">
          <Col lg={8} xl={9} className="px-5">
            <TitleWithLine text={t('contact')} />
            <p>{t('completeappform')}</p>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formEmail" className="mt-4 mt-md-0">
                    <Form.Label className="text-dark label-form">{t('namelastname')}</Form.Label>
                    <Controller
                      name="names"
                      control={control}
                      rules={{ required: { value: true, message: t('fieldrequired') } }}
                      render={({ field }) => <Form.Control {...field} type="text" placeholder={t('namelastname')} />}
                    />
                    {errors.names && <small className="text-danger">{errors.names.message}</small>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formEnterprise" className="mt-4 mt-md-0">
                    <Form.Label className="text-dark">{t('company')}</Form.Label>
                    <Controller
                      name="company"
                      control={control}
                      render={({ field }) => <Form.Control {...field} type="text" placeholder={t('company')} />}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formMail" className="mt-4 mt-md-3">
                    <Form.Label className="text-dark label-form">{t('email')}</Form.Label>
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: { value: true, message: t('fieldrequired') },
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: t('emailnotvalid'),
                        },
                      }}
                      render={({ field }) => <Form.Control {...field} type="email" placeholder={t('email')} />}
                    />
                    {errors.email && <small className="text-danger">{errors.email.message}</small>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formPhone" className="mt-4 mt-md-3">
                    <Form.Label className="text-dark label-form">{t('phone')}</Form.Label>
                    <Controller
                      name="phone"
                      control={control}
                      rules={{
                        required: { value: true, message: t('fieldrequired') },
                        pattern: {
                          value: /^[0-9\b]+$/,
                          message: t('solonumeros', { ns: 'backoffice' }),
                        },
                      }}
                      render={({ field }) => <Form.Control {...field} type="tel" placeholder={t('phone')} />}
                    />
                    {errors.phone && <small className="text-danger">{errors.phone.message}</small>}
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="FormDepartment" className="mt-4 mt-md-3">
                    <Form.Label className="text-dark label-form">{t('department')}</Form.Label>
                    <Controller
                      name="department"
                      control={control}
                      defaultValue=""
                      rules={{ required: { value: true, message: t('fieldrequired') } }}
                      render={({ field }) => (
                        <Form.Control {...field} as="select">
                          <option value="">{t('select')}</option>
                          <option value="info">{t('informationclient')}</option>
                          <option value="orders">{t('ordersebook')}</option>
                          <option value="returns">{t('returns')}</option>
                        </Form.Control>
                      )}
                    />
                    {errors.department && <small className="text-danger">{errors.department.message}</small>}
                  </Form.Group>
                </Col>
                {watch('department') === 'returns' && (
                  <>
                    <Col md={6}>
                      <Form.Group controlId="FormIBNS" className="mt-3">
                        <Form.Label className="text-dark label-form">{t('IBNS')}</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          placeholder={t('IBNS')}
                          {...register('ibns', {
                            required: t('fieldrequired'),
                            pattern: {
                              value: /^[0-9]+$/,
                              message: t('solonumeros', { ns: 'backoffice' }),
                            },
                          })}
                        />
                        {errors.ibns && <small className="text-danger">{errors.ibns.message}</small>}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="FormPedidoID" className="mt-3">
                        <Form.Label className="text-dark label-form"> ID {t('order', { ns: 'backoffice' })}</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          placeholder={`ID ${t('order', { ns: 'backoffice' })}`}
                          {...register('pedido_id', {
                            required: t('fieldrequired'),
                            pattern: {
                              value: /^[0-9]+$/,
                              message: t('solonumeros', { ns: 'backoffice' }),
                            },
                          })}
                        />
                        {errors.pedido_id && <small className="text-danger">{errors.pedido_id.message}</small>}
                      </Form.Group>
                    </Col>
                  </>
                )}
                <Col md={12}>
                  <Form.Group controlId="FormMessage" className="mt-4 mt-md-3">
                    <Form.Label className="text-dark label-form">{t('message')}</Form.Label>
                    <Controller
                      name="message"
                      control={control}
                      rules={{ required: { value: true, message: t('fieldrequired') } }}
                      render={({ field }) => (
                        <Form.Control {...field} as="textarea" rows={5} placeholder={t('message')} />
                      )}
                    />
                    {errors.message && <small className="text-danger">{errors.message.message}</small>}
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group controlId="formOffersText" className="d-flex mt-3">
                <Form.Label className="text-dark order-2 mx-3">{t('wantreceiveoffers')}</Form.Label>
                <Controller
                  name="recieveNews"
                  control={control}
                  render={({ field }) => <Form.Check {...field} checked={field.value} type="checkbox" />}
                />
              </Form.Group>
              <Form.Group controlId="formLegalText" className=" mt-3 d-flex">
                <Form.Label className="text-dark order-2 mx-3">
                  {t('understandlegalnotice')}{' '}
                  <Link to={`/${assignedLibrary}/aviso-legal`} className="text-urls-primary">
                    {t('legalnotice')}
                  </Link>{' '}
                  {t('andthe')}{' '}
                  <Link to={`/${assignedLibrary}/condiciones-de-uso`} className="text-urls-primary">
                    {t('conditionsuse')}
                  </Link>
                </Form.Label>
                <Controller
                  name="acceptPolicy"
                  control={control}
                  render={({ field }) => <Form.Check {...field} type="checkbox" checked={field.value} />}
                />
              </Form.Group>
              <Col sm={12} className="d-flex justify-content-center">
                <Button disabled={showCard} type="submit" className="mx-auto d-block  button-send mt-5 button-primary">
                  {buttonText}
                </Button>
              </Col>
            </Form>
            <div className="d-flex justify-content-center ">
              {requestStatus && typeof msg === 'string' && msg !== '' && (
                <FeedbackStatus
                  feedbackType={requestStatus}
                  isVisible={showCard}
                  msgObject={msg}
                  onClose={handleClose}
                />
              )}
            </div>
            <br></br>
            <p className="legal-info ">{t('filteresponsability')}</p>
          </Col>
          <Col lg={4} xl={3} className="side">
            <Row>
              <Col md={12} className="side-square1">
                <h2 className="page-title static-title mt-3">{t('usefuldata')}</h2>
                <p>{t('contactspecificdepartments')}</p>
                <p className="contact-info" style={{ display: `${correo !== '' ? 'block' : 'none'}` }}>
                  {t('informationclient')} <a href={`mailto: ${correo}`}> {correo}</a>
                </p>
                <p className="contact-info">
                  {t('phonescustomersupport')}
                  <br></br>
                  <Link to="#" style={{ display: `${telefono !== '' ? 'block' : 'none'}` }}>
                    {telefono}
                  </Link>
                  <br></br>
                  <span className="color-text-gray">{t('schedule')}</span>
                </p>
              </Col>
            </Row>
            <Row>
              <Col md={12} className="side-square2">
                <p className="contact-info">
                  {t('needcontact')}
                  <Link to={`/${assignedLibrary}/librerias`} className="mx-2">
                    {t('here')}
                  </Link>
                </p>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Contact;
